/**
* Hand-written per-locale copy for the /notary pillar mirrors. Each locale
* ships unique intro, benefits and FAQ — authored, not machine-translated —
* so the localized pillar satisfies E-E-A-T standalone. Locales: en zh ja ko vi id.
*/
export type NotaryLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface NotaryLocaleCopy {
  hreflang: string;
  title: string;
  description: string;
  h1: string;
  lead: string;
  benefits: Array<[string, string]>;
  faqs: Array<{ q: string; a: string }>;
  ctaCall: string;
  ctaLine: string;
  breadcrumbHome: string;
  breadcrumbNotary: string;
  seeAllActs: string;
  seeAllCities: string;
  seeAllCountries: string;
  /** Deep-dive sections rendered under FAQ to satisfy ≥1,200-word threshold. */
  sections?: Array<{ heading: string; paragraphs: string[] }>;
}

export const NOTARY_LOCALE_COPY: Record<NotaryLocale, NotaryLocaleCopy> = {
  en: {
    hreflang: "en",
    title: "Notary Public Thailand — Licensed Attorneys",
    description:
      "Notarial services in Thailand by Lawyers Council-registered attorneys: signature witnessing, true-copy, power of attorney, oath and translation certification, with destination requirements checked first.",
    h1: "Notary Public Thailand — Attested by Licensed Notarial Services Attorneys",
    lead:
      "Thai Notary Law provides notarial services through lawyers who hold the relevant Lawyers Council of Thailand qualification. The lawyer first checks the document, the signer’s identity and the receiving authority’s stated requirements, because notarisation, Thai consular legalisation and embassy certification are separate steps and not every destination asks for all three.",
    benefits: [
      ["Registered with the Lawyers Council", "Our Notarial Services Attorneys are listed in the public register and can be verified by license number on request."],
      ["Timing confirmed after review", "The required act, number of signers, originals and follow-on legalisation determine the schedule. We confirm an estimated completion window before work starts."],
      ["Written scope and quotation", "Professional fees and government or embassy charges are separated in writing after the documents and destination requirements are reviewed."],
      ["Multilingual", "Documents can be reviewed in Thai, English, Chinese, Japanese, Korean, Vietnamese and Indonesian; the language used for the notarial act is confirmed after document review."],
      ["Chain of authentication", "We can arrange follow-on MFA legalisation and embassy attestation so your document is ready for the destination jurisdiction."],
      ["Confidential handling", "Access to client documents is limited to the assigned team, and additional confidentiality arrangements can be discussed for corporate matters."],
    ],
    faqs: [
      { q: "Is a Thai Notarial Services Attorney the same as a US Notary Public?", a: "They operate under a different Thai professional framework. Acceptance and any further legalisation depend on the receiving authority and destination country." },
      { q: "How long is a notarised document valid?", a: "Notarisation confirms the act at a point in time; validity of the underlying document is set by the receiving authority. The receiving authority should be asked whether it imposes a recency requirement." },
      { q: "Can you notarise a document in English?", a: "Yes. We routinely notarise English powers of attorney, affidavits, corporate resolutions and consents drafted by overseas counsel." },
      { q: "Do you provide onsite notarisation?", a: "Yes, at hospitals, offices, hotels and Immigration Bureau appointments in Bangkok and provincial capitals for an additional travel fee." },
    ],
    ctaCall: "Call +66 94-895-8999 or add LINE ID @thainotarylaw",
    ctaLine: "Send the document and destination requirements for an initial scope and quotation during business hours.",
    breadcrumbHome: "Home",
    breadcrumbNotary: "Notary Public",
    seeAllActs: "All notarial acts",
    seeAllCities: "Coverage by city",
    seeAllCountries: "Accepted in these destinations",
    sections: [
      {
        heading: "What a Thai Notarial Services Attorney actually does",
        paragraphs: [
          "A Thai Notarial Services Attorney is an admitted lawyer who has passed the Lawyers Council of Thailand notarial training and is entered in the public notarial register. Under the B.E. 2546 Regulations on Notarial Services, we may witness the execution of documents, verify the identity of signatories, certify that copies are true and complete reproductions of the original, administer oaths and affirmations, take affidavits, receive protests of commercial paper, and attest to the authenticity of translations. Each of these acts creates evidence that a Thai lawyer, personally responsible under their practising certificate, saw the document, checked the signatory's identity against a passport or ID, and confirmed the act on the date shown on the notarial certificate.",
          "Because Thailand is not a party to the 1961 Hague Apostille Convention, an authenticated Thai document usually needs a second layer — legalisation by the Ministry of Foreign Affairs Consular Affairs Department and, when the destination requires it, endorsement by that country's embassy in Bangkok. We prepare notarial packs so that the MFA and embassy stages proceed without kickback: consistent names, matching passport numbers, correct capacity clauses for corporate signatories, and Thai translation attached whenever the destination is a Thai-language jurisdiction.",
        ],
      },
      {
        heading: "Typical use cases we handle every week",
        paragraphs: [
          "Overseas study — degree certificates, transcripts and English translations for universities in the United States, United Kingdom, Australia, Canada, Germany, Japan, Korea and Singapore, including the specific formats required by ECFMG, WES, ICAS and NARIC.",
          "Corporate cross-border filings — board resolutions, powers of attorney, certificates of good standing, apostille-equivalent chains for a Thai subsidiary signing contracts, opening bank accounts or registering intellectual property overseas.",
          "Immigration — statutory declarations of common-law relationship for Australian partner visas (subclass 820/801, 309/100), Canadian spousal sponsorships, UK settlement, and USCIS I-130 evidence.",
          "Real estate and inheritance — powers of attorney authorising a Thai representative to sign at the Land Department, and affidavits of heirship for foreign estates that hold Thai property.",
        ],
      },
      {
        heading: "How we protect the integrity of every notarial act",
        paragraphs: [
          "We only notarise in the presence of the signatory. Remote video notarisation is not recognised under Thai law and we refuse it even when clients offer to pay a premium. Signatories present a passport (foreign nationals) or Thai national ID card, and we retain a legible scan in the case file for the duration required by the Lawyers Council.",
          "For corporate acts we cross-check the signatory's authority in the affidavit of incorporation, board minutes, and — where available — the DBD company registration extract. If capacity is unclear we suspend the act and request a fresh resolution rather than issue a notarial certificate that later fails at the receiving authority.",
          "Every certificate carries a unique case number, wet signature, office seal and the attorney's Lawyers Council number. Receiving embassies and courts can call our office and confirm the act against our register.",
        ],
      },
    ],
  },
  zh: {
    hreflang: "zh-Hans",
    title: "泰国公证服务 — 律师公会注册公证律师 | Thai Notary Law",
    description: "由泰国律师公会注册的公证律师提供签名认证、副本认证、授权书、宣誓和翻译证明服务,并按接收机构要求核对认证步骤。",
    h1: "泰国公证服务 — 由持牌公证律师办理",
    lead:
      "Thai Notary Law 是根据佛历2546年公证律师条例经泰国律师公会授权注册的公证律师事务所,可对签名进行认证、出具副本证明、监誓、见证合同,并为需要提交至境外大使馆、移民局、法院、银行及大学的文件办理认证。每份公证文件均带有律师亲笔签名、印章及案件编号,便于接收方核实真伪。",
    benefits: [
      ["律师公会正式注册", "所有公证律师均可通过律师公会公开注册系统核验执业编号。"],
      ["审核后确认时间", "根据文件、签署人及后续认证步骤书面确认办理时间。"],
      ["书面报价", "审核文件与目的地要求后，区分专业费与政府或使馆费用。"],
      ["多语种服务", "可用中文、英文、泰文、日文、韩文、越南文和印尼文沟通；文件审核后确认公证书所用语言。"],
      ["认证一站式", "可衔接办理泰国外交部认证及使馆认证,直至目的地国家可用。"],
      ["限制访问", "文件仅由负责人员访问，企业事项可另行约定保密条件。"],
    ],
    faqs: [
      { q: "泰国公证律师与美国 Notary Public 有何区别?", a: "职能相当但依据法律不同。泰国公证律师是持有律师执照并取得公证专项资格的执业律师,是否接受及是否需要后续认证，由接收机构和目的地规则决定。" },
      { q: "公证书的有效期是多久?", a: "公证仅证明特定时点的法律行为,原文件效力由接收机关决定。应向接收机构确认是否有文件时效要求。" },
      { q: "可以公证英文文件吗?", a: "可以。我们经常为境外律师起草的英文授权书、宣誓书、董事会决议及同意函办理公证。" },
      { q: "是否提供上门公证?", a: "是。曼谷及各府会城市的医院、办公室、酒店及移民局现场公证均可安排,酌收出行费用。" },
    ],
    ctaCall: "电话 +66 94-895-8999 或添加 LINE @thainotarylaw",
    ctaLine: "请发送文件及接收机构要求；审核后确认范围、费用和预计时间。",
    breadcrumbHome: "首页",
    breadcrumbNotary: "公证服务",
    seeAllActs: "全部公证事项",
    seeAllCities: "城市覆盖",
    seeAllCountries: "适用目的地",
    sections: [
      {
        heading: "泰国公证律师的法定职权",
        paragraphs: [
          "泰国公证律师是通过律师公会公证专项培训并登记在册的执业律师。依据佛历2546年《公证律师规则》,我们可见证文件签署、核实签署人身份、出具真正副本证明、主持宣誓与确认、受理宣誓陈述、办理商业票据拒付证明,以及证明翻译文本与原文一致。每一项行为都由承担执业责任的泰国律师亲自完成,并在公证证书上载明当日核对护照或身份证的事实,构成受理机关可直接采信的法律证据。",
          "由于泰国尚未加入1961年《海牙认证公约》,经公证的文件通常还需经泰国外交部领事司加签,以及目的地国家驻曼谷使馆的领事认证。我们会预先编排整套认证材料,确保姓名、护照号码、公司代表人身份表述及泰文对照译本一次通过,避免使馆退件。",
        ],
      },
      {
        heading: "常见办理场景",
        paragraphs: [
          "海外升学 — 学位证书、成绩单及英文翻译供美国、英国、澳大利亚、加拿大、德国、日本、韩国及新加坡的大学使用,包括 ECFMG、WES、ICAS、NARIC 所要求的特定格式。",
          "跨境公司事务 — 董事会决议、授权委托书、良好信誉证书,以及泰国子公司签署海外合同、开设银行账户或办理知识产权登记所需的完整认证链。",
          "移民签证 — 澳大利亚伴侣签证(820/801、309/100)、加拿大配偶担保、英国长期居留及美国 USCIS I-130 所要求的事实同居声明。",
          "不动产与继承 — 授权他人代为在土地厅签署文件的委托书,以及涉及泰国境内不动产的境外遗产继承人身份声明。",
        ],
      },
      {
        heading: "公证品质的保障机制",
        paragraphs: [
          "所有公证必须在签署人本人到场情况下办理。泰国法律不承认远程视频公证,即便客户愿意支付溢价我们亦不接受。签署人须出示护照(外籍)或泰国身份证(泰籍),事务所依律师公会要求存档。",
          "公司行为需交叉核对公司章程、董事会决议及泰国商业发展厅(DBD)登记摘要中的授权范围。若代表权限存疑,我们将暂停出具公证证书并要求补充新的决议,而非签发日后可能被受理机关退回的文件。",
          "每份证书均编有唯一案件号,并加盖印章与律师亲笔签名,注明律师公会执业编号。海外使馆或法院可致电本所核对底册,验证公证真实性。",
        ],
      },
    ],
  },
  ja: {
    hreflang: "ja",
    title: "タイ公証サービス — 弁護士会登録の公証弁護士 | Thai Notary Law",
    description: "タイ弁護士会登録の公証弁護士が署名認証、謄本認証、委任状、宣誓、翻訳証明を提供。提出先の要件に応じて認証手順を確認します。",
    h1: "タイ公証サービス — 登録公証弁護士が対応",
    lead:
      "Thai Notary Law は仏暦2546年公証弁護士規則に基づきタイ弁護士会に登録された公証弁護士事務所です。海外の大使館、入国管理局、裁判所、銀行、大学に提出する書類について、署名認証、真正謄本証明、宣誓、契約立会いを行います。すべての公証書には弁護士の直筆署名、押印、案件番号が付されており、受領機関から真偽照会があった際に検証可能です。",
    benefits: [
      ["弁護士会公式登録", "登録番号は弁護士会の公開登録簿で照会できます。"],
      ["確認後に日程提示", "書類・署名者・追加認証を確認してから所要期間を提示します。"],
      ["書面見積り", "書類確認後、専門費用と公的・大使館費用を分けて提示します。"],
      ["多言語対応", "日本語、英語、タイ語、中国語、韓国語、ベトナム語、インドネシア語でご相談いただけます。公証書の使用言語は書類確認後に確定します。"],
      ["ワンストップ認証", "タイ外務省認証および大使館認証まで一括手配。"],
      ["アクセス制限", "担当者のみが書類にアクセスし、法人案件では追加の秘密保持を協議できます。"],
    ],
    faqs: [
      { q: "タイの公証弁護士と日本の公証人の違いは?", a: "根拠法は異なりますが機能はほぼ同等です。タイの公証弁護士はタイ弁護士会が認定した弁護士で、受理の可否や追加認証は、提出先機関と国の規則によって異なります。" },
      { q: "公証書の有効期間は?", a: "公証は特定時点の行為を証明します。原文書の有効性は受領機関の規定によります。提出先機関に書類の発行時期要件をご確認ください。" },
      { q: "英語書類も公証できますか?", a: "はい。海外の弁護士が起草した英文の委任状、宣誓書、取締役会議事録、同意書を日常的に公証しています。" },
      { q: "出張公証はありますか?", a: "はい。バンコクおよび地方主要都市の病院、オフィス、ホテル、入国管理局窓口への出張公証を有償で承ります。" },
    ],
    ctaCall: "電話 +66 94-895-8999 · LINE ID @thainotarylaw",
    ctaLine: "書類と提出先要件をお送りください。確認後に対応範囲をご案内します。",
    breadcrumbHome: "ホーム",
    breadcrumbNotary: "公証サービス",
    seeAllActs: "全ての公証行為",
    seeAllCities: "対応都市",
    seeAllCountries: "受理国一覧",
    sections: [
      {
        heading: "タイ公証弁護士の職務範囲",
        paragraphs: [
          "タイ公証弁護士は、タイ弁護士会の公証専門研修を修了し公開登録簿に登載された弁護士です。仏暦2546年公証弁護士規則に基づき、文書の署名立会い、署名者本人確認、真正謄本の証明、宣誓・確認の主宰、宣誓供述の受理、手形拒絶証書の作成、翻訳の一致証明を行います。各行為は執業証書を有する弁護士が自ら実施し、旅券又は身分証で確認した事実とともに公証書に記載され、受領機関が直接依拠できる法的証拠となります。",
          "タイは1961年ハーグ認証条約非加盟のため、公証済文書はさらにタイ外務省領事局の認証と、必要に応じ在バンコク当該国大使館の領事認証を要します。当事務所は氏名・旅券番号・法人代表資格・タイ語対訳を事前に整合させ、外務省及び大使館段階での差戻しを防ぎます。",
        ],
      },
      {
        heading: "毎週対応する典型的な利用場面",
        paragraphs: [
          "海外進学 — 学位記・成績証明書とその英訳を米国・英国・豪州・カナダ・独国・日本・韓国・シンガポールの大学向けに整備し、ECFMG・WES・ICAS・NARICが要求する形式に対応。",
          "国際企業実務 — 取締役会決議、委任状、良好存続証明、タイ子会社が海外契約締結・銀行口座開設・知的財産登録を行うための認証チェーン。",
          "移民ビザ — 豪州パートナービザ(820/801、309/100)、カナダ配偶者スポンサー、英国永住、米国USCIS I-130に必要な事実婚宣誓供述。",
          "不動産・相続 — 土地局での代理署名を可能にする委任状、及びタイ国内不動産を含む海外遺産の相続人身分宣誓供述。",
        ],
      },
      {
        heading: "公証の完全性を守る運用",
        paragraphs: [
          "公証は必ず署名者ご本人の面前で行います。タイ法はビデオ公証を認めておらず、割増料金の提示があっても対応しません。署名者は旅券(外国籍)又はタイ国民ID(タイ国籍)を提示し、鮮明な写しを弁護士会所定期間保管します。",
          "法人行為では、定款・取締役会議事録・DBD(商業開発局)登記情報で代表権を交差確認します。権限が不明確な場合は公証を停止し、新たな決議を求めます。後日受領機関で差し戻される公証書を発行することはありません。",
          "全ての公証書には固有の案件番号、押印、弁護士自筆署名及び弁護士会登録番号が記載されます。海外大使館・裁判所は当事務所へ直接照会し登録簿と照合できます。",
        ],
      },
    ],
  },
  ko: {
    hreflang: "ko",
    title: "태국 공증 서비스 — 변호사협회 등록 공증변호사 | Thai Notary Law",
    description: "태국 변호사협회 등록 공증변호사가 서명 인증, 사본 인증, 위임장, 선서, 번역 인증 서비스를 제공하며 접수 기관 요건에 따라 인증 단계를 확인합니다.",
    h1: "태국 공증 서비스 — 등록 공증변호사가 직접 처리",
    lead:
      "Thai Notary Law는 불기 2546년 공증변호사 규정에 따라 태국 변호사협회에 등록된 공증변호사 사무소로, 해외의 대사관, 이민국, 법원, 은행, 대학에 제출할 문서에 대해 서명 인증, 진정 사본 인증, 선서 집행, 계약 입회 등의 공증을 수행합니다. 모든 공증 문서에는 변호사 자필 서명, 인장, 사건 번호가 표기되어 접수 기관이 진위를 언제든 확인할 수 있습니다.",
    benefits: [
      ["변호사협회 정식 등록", "공증변호사 등록번호는 협회 공개 등록부에서 조회 가능합니다."],
      ["검토 후 일정 확정", "문서·서명자·후속 인증을 확인한 뒤 처리 일정을 서면으로 안내합니다."],
      ["서면 견적", "문서 검토 후 전문 수수료와 정부·대사관 비용을 구분해 안내합니다."],
      ["다국어 서비스", "한국어, 태국어, 영어, 중국어, 일본어, 베트남어, 인도네시아어로 상담할 수 있으며 공증 문서 언어는 검토 후 확정합니다."],
      ["원스톱 인증", "태국 외무부 인증 및 대사관 인증까지 일괄 대행."],
      ["접근 제한", "담당자만 문서에 접근하며 기업 업무는 추가 비밀유지 조건을 협의할 수 있습니다."],
    ],
    faqs: [
      { q: "태국 공증변호사와 한국 공증인의 차이는?", a: "근거 법률은 다르지만 기능은 동등합니다. 태국 공증변호사는 변호사협회가 인증한 변호사로, 수리 여부와 추가 인증 필요성은 접수 기관 및 목적지 규정에 따라 달라집니다." },
      { q: "공증서 유효 기간은?", a: "공증은 특정 시점의 행위를 증명합니다. 원본 서류의 유효 기간은 접수 기관의 기준에 따르며, 접수 기관에 문서 발급 시점 요건을 확인해야 합니다." },
      { q: "영문 서류도 공증 가능한가요?", a: "가능합니다. 해외 변호사가 작성한 영문 위임장, 진술서, 이사회 결의서, 동의서 등을 일상적으로 공증합니다." },
      { q: "출장 공증도 되나요?", a: "네. 방콕 및 지방 주요 도시의 병원, 사무실, 호텔, 이민국 창구 출장 공증이 유상으로 가능합니다." },
    ],
    ctaCall: "전화 +66 94-895-8999 · LINE @thainotarylaw",
    ctaLine: "문서와 접수 기관 요건을 보내면 검토 후 업무 범위를 안내합니다.",
    breadcrumbHome: "홈",
    breadcrumbNotary: "공증 서비스",
    seeAllActs: "전체 공증 행위",
    seeAllCities: "도시별 안내",
    seeAllCountries: "접수 국가",
    sections: [
      {
        heading: "태국 공증변호사의 법정 권한",
        paragraphs: [
          "태국 공증변호사는 태국 변호사협회의 공증 전문 교육을 이수하고 공개 등록부에 등재된 현직 변호사입니다. 불기 2546년 공증변호사 규정에 따라 문서 서명 입회, 서명자 본인 확인, 진정 사본 인증, 선서 및 확인 주재, 진술서 접수, 상업어음 거절증서 작성, 번역문 일치 확인을 수행할 수 있습니다. 각 행위는 집행 책임을 지는 태국 변호사가 직접 수행하며 여권 또는 신분증 확인 사실과 함께 공증서에 명시되어, 접수 기관이 직접 신뢰할 수 있는 법적 증거가 됩니다.",
          "태국은 1961년 헤이그 아포스티유 협약 비가입국이므로, 공증 문서는 대개 태국 외교부 영사국의 인증과 필요한 경우 주태국 해당국 대사관의 영사 인증이 추가로 필요합니다. 본 사무소는 성명·여권번호·법인 대표 자격·태국어 대역을 사전에 정합시켜 외교부 및 대사관 단계에서 반려되지 않도록 준비합니다.",
        ],
      },
      {
        heading: "매주 처리하는 대표 사례",
        paragraphs: [
          "해외 진학 — 학위증·성적증명서 및 영문 번역을 미국·영국·호주·캐나다·독일·일본·한국·싱가포르 대학에 제출, ECFMG·WES·ICAS·NARIC이 요구하는 형식을 준수합니다.",
          "국제 기업 업무 — 이사회 결의서, 위임장, 존속증명, 태국 자회사의 해외 계약 체결·은행 계좌 개설·지식재산 등록을 위한 인증 체인.",
          "이민 비자 — 호주 파트너 비자(820/801, 309/100), 캐나다 배우자 스폰서, 영국 정주, 미국 USCIS I-130에 필요한 사실혼 진술.",
          "부동산·상속 — 토지국에서 대리 서명을 위한 위임장, 태국 내 부동산이 포함된 해외 상속인 신분 진술.",
        ],
      },
      {
        heading: "공증 무결성 보장 절차",
        paragraphs: [
          "모든 공증은 서명자 본인 대면으로 이루어집니다. 태국 법은 화상 공증을 인정하지 않으며, 프리미엄 요금 제안이 있어도 응하지 않습니다. 서명자는 여권(외국인) 또는 태국 신분증(태국인)을 제시하고, 본 사무소는 변호사협회 규정 기간 동안 사본을 보관합니다.",
          "법인 행위의 경우 정관, 이사회 의사록, DBD(상무개발국) 등록부에서 대표 권한을 교차 확인합니다. 권한이 불명확하면 공증을 보류하고 새 결의서를 요청하며, 접수 기관에서 반려될 공증서를 발급하지 않습니다.",
          "모든 공증서에는 고유 사건번호, 인장, 변호사 자필 서명, 변호사협회 등록번호가 기재됩니다. 해외 대사관 및 법원은 본 사무소에 직접 조회하여 등록부와 대조할 수 있습니다.",
        ],
      },
    ],
  },
  vi: {
    hreflang: "vi",
    title: "Công chứng Thái Lan — Luật sư đăng ký",
    description: "Luật sư công chứng đăng ký Liên đoàn Luật sư Thái cung cấp chứng thực chữ ký, bản sao, ủy quyền, tuyên thệ và bản dịch, và kiểm tra yêu cầu của cơ quan tiếp nhận trước khi thực hiện.",
    h1: "Dịch vụ công chứng Thái Lan — Do luật sư công chứng được cấp phép thực hiện",
    lead:
      "Thai Notary Law là văn phòng luật sư công chứng đăng ký với Liên đoàn Luật sư Thái Lan theo quy định năm B.E. 2546, được ủy quyền chứng thực chữ ký, xác nhận bản sao đúng nguyên bản, tổ chức tuyên thệ và làm chứng hợp đồng cho các tài liệu nộp cho đại sứ quán, cơ quan xuất nhập cảnh, tòa án, ngân hàng và trường đại học ở nước ngoài. Mọi văn bản công chứng đều mang chữ ký tươi, dấu nổi và mã hồ sơ để cơ quan tiếp nhận có thể xác minh.",
    benefits: [
      ["Đăng ký chính thức", "Số giấy phép luật sư công chứng có thể tra cứu tại Liên đoàn Luật sư."],
      ["Xác nhận sau khi xem hồ sơ", "Thời gian được xác nhận sau khi kiểm tra tài liệu, người ký và bước hợp pháp hóa tiếp theo."],
      ["Báo giá bằng văn bản", "Tách phí chuyên môn và phí cơ quan nhà nước hoặc đại sứ quán sau khi xem hồ sơ."],
      ["Đa ngôn ngữ", "Soạn thảo và công chứng bằng tiếng Việt, Thái, Anh, Trung, Nhật, Hàn, Indonesia."],
      ["Chuỗi hợp pháp hóa", "Kết nối trực tiếp với Bộ Ngoại giao Thái và đại sứ quán để hoàn tất chuỗi xác thực."],
      ["Giới hạn truy cập", "Chỉ nhóm phụ trách được truy cập; có thể thỏa thuận bảo mật riêng cho hồ sơ doanh nghiệp."],
    ],
    faqs: [
      { q: "Luật sư công chứng Thái khác gì công chứng viên Việt Nam?", a: "Khác nhau về căn cứ pháp lý nhưng chức năng tương đương. Luật sư công chứng Thái là luật sư có chứng chỉ công chứng do Liên đoàn Luật sư cấp, việc chấp nhận và bước hợp pháp hóa tiếp theo phụ thuộc cơ quan tiếp nhận và quốc gia đích." },
      { q: "Bản công chứng có hiệu lực bao lâu?", a: "Công chứng xác nhận hành vi tại một thời điểm; hiệu lực của tài liệu gốc do cơ quan tiếp nhận quy định. Cần hỏi cơ quan tiếp nhận xem họ có yêu cầu về thời điểm cấp hay không." },
      { q: "Có công chứng tài liệu tiếng Anh không?", a: "Có. Chúng tôi thường xuyên công chứng giấy ủy quyền, tuyên thệ, nghị quyết công ty và văn bản đồng ý bằng tiếng Anh." },
      { q: "Có dịch vụ công chứng tại chỗ không?", a: "Có, tại bệnh viện, văn phòng, khách sạn và cửa xuất nhập cảnh ở Bangkok và các thành phố lớn, có phụ phí di chuyển." },
    ],
    ctaCall: "Gọi +66 94-895-8999 hoặc LINE @thainotarylaw",
    ctaLine: "Gửi tài liệu và yêu cầu của cơ quan tiếp nhận để xác nhận phạm vi.",
    breadcrumbHome: "Trang chủ",
    breadcrumbNotary: "Công chứng",
    seeAllActs: "Tất cả nghiệp vụ công chứng",
    seeAllCities: "Theo thành phố",
    seeAllCountries: "Được chấp nhận tại",
  },
  id: {
    hreflang: "id",
    title: "Notaris Thailand — Pengacara Terdaftar",
    description: "Pengacara notaris terdaftar Dewan Advokat Thailand: legalisasi tanda tangan, salinan asli, surat kuasa, sumpah & terjemahan — dengan langkah autentikasi yang diperiksa sesuai instansi penerima.",
    h1: "Layanan Notaris Thailand — Ditangani Pengacara Notaris Berlisensi",
    lead:
      "Thai Notary Law adalah kantor pengacara notaris yang terdaftar di Dewan Pengacara Thailand berdasarkan peraturan B.E. 2546, berwenang mengesahkan tanda tangan, menerbitkan salinan sesuai asli, mengambil sumpah, dan menyaksikan penandatanganan kontrak untuk dokumen yang akan diajukan ke kedutaan besar, imigrasi, pengadilan, bank, dan universitas di luar negeri. Setiap dokumen notarial dilengkapi tanda tangan basah, cap kantor, dan nomor perkara sehingga instansi penerima dapat memverifikasi keasliannya.",
    benefits: [
      ["Terdaftar resmi", "Nomor lisensi pengacara notaris dapat diverifikasi di daftar publik Dewan Pengacara."],
      ["Jadwal setelah pemeriksaan", "Waktu dipastikan setelah dokumen, penandatangan, dan tahap legalisasi lanjutan diperiksa."],
      ["Penawaran tertulis", "Biaya profesional dipisahkan dari biaya pemerintah atau kedutaan setelah dokumen diperiksa."],
      ["Multibahasa", "Konsultasi tersedia dalam bahasa Indonesia, Thai, Inggris, Mandarin, Jepang, Korea, dan Vietnam; bahasa akta dikonfirmasi setelah dokumen diperiksa."],
      ["Rantai legalisasi", "Terhubung dengan Kemenlu Thailand dan kedutaan untuk otentikasi berjenjang."],
      ["Akses terbatas", "Hanya tim yang ditugaskan dapat mengakses dokumen; ketentuan kerahasiaan tambahan dapat dibahas."],
    ],
    faqs: [
      { q: "Apa beda pengacara notaris Thailand dengan notaris Indonesia?", a: "Landasan hukum dan ruang lingkup kewenangannya berbeda. Pengacara notaris Thailand adalah advokat dengan kualifikasi notarial; penerimaan dokumen dan legalisasi lanjutan ditentukan oleh instansi penerima." },
      { q: "Berapa lama dokumen notarial berlaku?", a: "Notarial mengesahkan tindakan pada satu titik waktu; masa berlaku dokumen aslinya mengikuti ketentuan instansi penerima. Tanyakan kepada instansi penerima apakah ada ketentuan mengenai tanggal penerbitan." },
      { q: "Bisakah dokumen berbahasa Inggris dinotarialkan?", a: "Bisa. Kami rutin menotarialkan surat kuasa, pernyataan sumpah, resolusi perusahaan, dan surat persetujuan berbahasa Inggris." },
      { q: "Apakah ada layanan notaris di lokasi?", a: "Ya, di rumah sakit, kantor, hotel, dan loket imigrasi di Bangkok dan kota besar lainnya dengan biaya perjalanan." },
    ],
    ctaCall: "Telepon +66 94-895-8999 atau LINE @thainotarylaw",
    ctaLine: "Kirim dokumen dan syarat instansi penerima untuk memastikan cakupan.",
    breadcrumbHome: "Beranda",
    breadcrumbNotary: "Notaris",
    seeAllActs: "Semua tindakan notarial",
    seeAllCities: "Cakupan kota",
    seeAllCountries: "Diterima di",
    sections: [
      {
        heading: "Kewenangan hukum pengacara notaris Thailand",
        paragraphs: [
          "Pengacara notaris Thailand adalah advokat yang telah menyelesaikan pelatihan notarial Dewan Pengacara Thailand dan terdaftar di daftar publik. Berdasarkan Peraturan Pengacara Notaris B.E. 2546, kami dapat menyaksikan penandatanganan dokumen, memverifikasi identitas penandatangan, mengesahkan salinan sesuai asli, memimpin sumpah dan afirmasi, menerima pernyataan sumpah, membuat akta protes surat berharga, serta mengesahkan kesesuaian terjemahan. Setiap tindakan dilakukan langsung oleh advokat pemegang izin praktik dan dicatat pada sertifikat notarial bersama fakta pemeriksaan paspor atau KTP, membentuk bukti hukum yang dapat langsung dijadikan sandaran instansi penerima.",
          "Karena Thailand belum menjadi pihak dalam Konvensi Apostille Den Haag 1961, dokumen ternotarialisasi umumnya masih memerlukan legalisasi Direktorat Konsuler Kemenlu Thailand dan, bila diperlukan, legalisasi konsuler kedutaan negara tujuan di Bangkok. Kami menyelaraskan nama, nomor paspor, kapasitas pejabat perusahaan, dan terjemahan bahasa Thai sejak awal agar tahap Kemenlu dan kedutaan tidak dikembalikan.",
        ],
      },
      {
        heading: "Kasus penggunaan yang kami tangani setiap minggu",
        paragraphs: [
          "Pendidikan luar negeri — ijazah, transkrip, dan terjemahan bahasa Inggris untuk universitas di Amerika Serikat, Inggris, Australia, Kanada, Jerman, Jepang, Korea, dan Singapura, termasuk format khusus ECFMG, WES, ICAS, dan NARIC.",
          "Urusan korporasi lintas negara — resolusi direksi, surat kuasa, sertifikat status baik, rantai autentikasi untuk anak perusahaan Thailand yang menandatangani kontrak, membuka rekening bank, atau mendaftarkan kekayaan intelektual di luar negeri.",
          "Imigrasi — pernyataan sumpah hubungan de facto untuk visa pasangan Australia (subclass 820/801, 309/100), sponsor pasangan Kanada, settlement Inggris, dan USCIS I-130.",
          "Real estat dan waris — surat kuasa untuk penandatanganan di Kantor Pertanahan, serta pernyataan ahli waris untuk harta warisan luar negeri yang mencakup aset Thailand.",
        ],
      },
      {
        heading: "Menjaga integritas setiap tindakan notarial",
        paragraphs: [
          "Semua notarial dilakukan di hadapan penandatangan. Hukum Thailand tidak mengakui notarial video jarak jauh, dan kami menolak walaupun ditawarkan biaya lebih. Penandatangan menunjukkan paspor (WNA) atau KTP Thailand (WN Thai), dan salinannya kami simpan sesuai ketentuan Dewan Pengacara.",
          "Untuk tindakan korporasi kami memeriksa silang kewenangan pada anggaran dasar, risalah direksi, dan ekstrak pendaftaran DBD. Bila kewenangan tidak jelas, tindakan ditunda dan resolusi baru diminta — bukan menerbitkan sertifikat yang berisiko ditolak instansi penerima.",
          "Setiap sertifikat memuat nomor perkara unik, cap kantor, tanda tangan basah advokat, dan nomor lisensi Dewan Pengacara. Kedutaan dan pengadilan luar negeri dapat menghubungi kantor kami untuk mencocokkan dengan register.",
        ],
      },
    ],
  },
};
